import React, { type FormEvent, useState } from "react";

import type {
  OverlayState,
  PasswordFormValues,
  UserRecord,
} from "../../../features/users/userTypes";
import Input from "../../controls/Input";
import { useForm } from "../useForm";

const initialValues: PasswordFormValues = {
  new_password: "",
  new_password_confirmation: "",
};

interface ChangePasswordFormProps {
  user: UserRecord;
  setOpen: (state: OverlayState) => void;
  onChangePassword: (values: PasswordFormValues) => void;
}

export default function ChangePasswordForm({
  user,
  setOpen,
  onChangePassword,
}: ChangePasswordFormProps) {
  const { values, handleInputChange } = useForm(initialValues, false);
  const [validationErrors, setValidationErrors] = useState<
    Partial<Record<keyof PasswordFormValues, string>>
  >({});

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();

    const nextErrors: Partial<Record<keyof PasswordFormValues, string>> = {};
    if (!values.new_password) {
      nextErrors.new_password = "New password is required";
    }
    if (!values.new_password_confirmation) {
      nextErrors.new_password_confirmation =
        "Password confirmation is required";
    } else if (values.new_password_confirmation !== values.new_password) {
      nextErrors.new_password_confirmation = "Passwords do not match";
    }

    if (Object.keys(nextErrors).length > 0) {
      setValidationErrors(nextErrors);
      return;
    }

    onChangePassword(values);
  };

  const close = () => setOpen({ childComponent: null, open: false });

  return (
    <div>
      <div className="px-4 py-5 sm:p-6">
        <h3 className="mb-4 text-lg font-medium leading-6 text-gray-900">
          Change Password for {user.name}
        </h3>
        <form onSubmit={handleSubmit}>
          <div className="space-y-4">
            <Input
              id="new_password"
              label="New Password"
              type="password"
              name="new_password"
              value={values.new_password}
              labelOn={true}
              onChange={handleInputChange}
              error={validationErrors.new_password}
            />
            <Input
              id="new_password_confirmation"
              label="Confirm New Password"
              type="password"
              name="new_password_confirmation"
              value={values.new_password_confirmation}
              onChange={handleInputChange}
              labelOn={true}
              error={validationErrors.new_password_confirmation}
            />
          </div>
          <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
            <button
              type="submit"
              className="inline-flex w-full justify-center rounded-md bg-main-purple px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 sm:col-start-2"
            >
              Change Password
            </button>
            <button
              type="button"
              onClick={close}
              className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
